import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/api";

// tiny helper to read role from token payload or a saved key
function getRole() {
  // if you saved it at login:
  const storedRole = localStorage.getItem("sp_role");
  if (storedRole) return storedRole;

  // otherwise decode token payload (best-effort)
  const tok = localStorage.getItem("sp_token");
  if (!tok) return "user";
  try {
    const payload = JSON.parse(atob(tok.split(".")[1] || ""));
    return payload?.role || "user";
  } catch {
    return "user";
  }
}

const calculateVendorEarnings = (order) => {
  if (!order?.items?.length) return 0;

  return order.items.reduce((sum, item) => {
    const price = Number(item.basePrice ?? item.pricePerUnit ?? item.price ?? 0);
    const qty   = Number(item.quantity ?? 0);
    return sum + price * qty;
  }, 0);
};

export const AllOrders = () => {
  const navigate = useNavigate();
  const [active, setActive] = useState("All");
  const filters = ["All", "Pending", "Dispatched", "Cancelled"];
  const [riderPhone, setRiderPhone] = useState(null);
  const [ridersMap, setRidersMap] = useState({});

  const [orders, setOrders] = useState([]);   // always store arrays here
  const [loading, setLoading] = useState(true);
  const role = getRole(); 
  

  const toPathByStatus = (status) => {
    if (status === "pending") return "/orders/pending";
    if (["processing", "shipped", "delivered"].includes(status)) return "/orders/complete";
    if (status === "cancelled") return "/orders/cancelled";
    return "/orders";
  };

  // Single effect: choose endpoint by role and normalize the shape
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        let res;
        if (role === "vendor" || role === "admin") {
          res = await api.orders.vendorMine();
        } else {
          res = await api.orders.mine();
        }

        // Normalize to an array safely
        // - buyer endpoint often returns { status, count, orders: [...] }
        // - vendor endpoint might return [...] directly (or also an object)
        const list = Array.isArray(res)
          ? res
          : Array.isArray(res?.orders)
          ? res.orders
          : [];

        setOrders(list);
      } catch (e) {
        console.warn("Failed to load orders:", e?.message || e);
        setOrders([]); // keep it an array
      } finally {
        setLoading(false);
      }
    })();
  }, [role]);

  // Use a guarded list everywhere so .filter never runs on non-arrays
  const list = Array.isArray(orders) ? orders : [];

  const filtered = useMemo(() => {
    if (active === "All") return list;
    if (active === "Pending")   return list.filter(o => o.status === "pending");
    if (active === "Dispatched") return list.filter(o => ["shipped","delivered"].includes(o.status));
    if (active === "Cancelled")  return list.filter(o => o.status === "cancelled");
    return list;
  }, [list, active]);

  const total = list.length;
  const pend  = list.filter(o => o.status === "pending").length;
  const disp  = list.filter(o => ["shipped","delivered"].includes(o.status)).length;
  const canc  = list.filter(o => o.status === "cancelled").length;


// LOAD ALL RIDERS ONCE & MAP THEM
useEffect(() => {
  const loadRiders = async () => {
    try {
      const res = await api.riders.getRiders();
      if (res.status && Array.isArray(res.data)) {
        // convert list → object for FAST lookup
        const map = {};
        res.data.forEach(r => {
          map[r.userId] = r; // rider.userId matches order.riderId
        });
        setRidersMap(map);
      }
    } catch (err) {
      console.log("Error loading riders:", err);
    }
  };

  loadRiders();
}, []);


  return (
    <div className="min-h-screen grid place-items-center bg-[#fff8e1]">
      <div className="app-container [font-family:'Nunito',Helvetica] text-[#5d4037] pb-40">

        {/* Topbar */}
        <div className="w-full flex items-center mt-4">
          <button onClick={() => navigate(-1)} className="ml-4 w-9 h-9 rounded-full bg-[#ccb357] grid place-items-center" aria-label="Back">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
          <h1 className="flex-1 text-center font-bold text-[18px]">All Orders</h1>
          <div className="w-9" />
        </div>

        {/* Search (TODO)*/}
        <div className="px-4 mt-4">
          <div className="relative">
            <input
              placeholder="Search Order"
              className="w-full h-[44px] pl-10 pr-4 rounded-full border border-[#5d4037] bg-[#fff8e1] outline-none"
            />
            <svg className="absolute left-3 top-1/2 -translate-y-1/2" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" stroke="#5d4037" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3 mt-4">
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#666] mr-2 align-middle" />Total Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{total}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#e7a23a] mr-2 align-middle" />Pending Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{pend}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#6bbf59] mr-2 align-middle" />Dispatched Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{disp}</p>
            </div>
            <div className="rounded-[12px] bg-[#eadfb7] p-3">
              <p className="text-[13px]"><span className="inline-block w-2 h-2 rounded-full bg-[#c2452d] mr-2 align-middle" />Cancelled Orders</p>
              <p className="mt-1 font-extrabold text-[18px]">{canc}</p>
            </div>
          </div>

          {/* Filters */}
          <div className="flex gap-3 mt-4">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                className={`px-3 py-1 rounded-[12px] text-[13px] ${
                  active === f ? "bg-[#5d4037] text-[#fff8e1]" : "bg-[#eadfb7] text-[#5d4037]"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* Orders list */}
        <div className="px-4 mt-4 space-y-4">
          {loading && <div className="text-sm opacity-80">Loading orders…</div>}
          {!loading && filtered.length === 0 && <div className="text-sm opacity-80">No orders.</div>}
          {!loading && filtered.map((o) => {
            const when = new Date(o.createdAt).toLocaleString();
            const amount = `₦${calculateVendorEarnings(o).toLocaleString()}`;
            const status = o.status || "pending";

            // NEW: find assigned rider
            const rider = ridersMap[o.riderId];

            let stripe = "#e7c85a", pillBg="#f7e8a6", pillText="#7d5d00";
            if (status === "cancelled") { stripe="#c2452d"; pillBg="#f5c5c1"; pillText="#a23526"; }
            if (["processing","shipped","delivered"].includes(status)) { stripe="#6bbf59"; pillBg="#cfeccb"; pillText="#2f6b2b"; }

            return (
             <button
  key={o.orderId || o._id}
  onClick={() => navigate(`/orders/${o.orderId}`)}
  className="w-full flex gap-3 bg-white rounded-xl p-3 shadow-sm border border-[#eee]"
>
  {/* Product Image */}
  <img
  src={
    o.items?.[0]?.productId?.images?.[0] ||
    "/placeholder.png"
  }
  alt="product"
  className="w-16 h-16 rounded-lg object-cover"
/>

  {/* Info */}
  <div className="flex-1 text-left">
    <p className="font-semibold text-[14px]">
      Order #{o.orderId || o._id}
    </p>

    <p className="text-[12px] text-gray-500">
      {(o.items || []).length} items
    </p>

    <p className="text-[12px] text-gray-400">
      {new Date(o.createdAt).toLocaleDateString()}
    </p>

    {/* Status */}
    <span className={`text-[11px] px-2 py-1 rounded-full mt-1 inline-block
      ${status === "pending" && "bg-yellow-100 text-yellow-700"}
      ${status === "cancelled" && "bg-red-100 text-red-600"}
      ${["processing","shipped","delivered"].includes(status) && "bg-green-100 text-green-700"}
    `}>
      {status}
    </span>
  </div>

  {/* Price */}
  <p className="font-bold text-[14px] whitespace-nowrap">
    ₦{calculateVendorEarnings(o).toLocaleString()}
  </p>
</button>

            );
          })}
        </div>

        {/* Safe-area bar */}
        <div className="fixed bottom-3 md:bottom-6 left-1/2 -translate-x-1/2 w-full max-w-[1440px] xl:max-w-[1600px] px-6 md:px-10 xl:px-16">
          <div className="h-[22px] bg-[#5d4037] flex items-center justify-center">
            <div className="w-[120px] h-[6px] rounded-full bg-white" />
          </div>
        </div>
      </div>
    </div>
  );
};
